// src/components/EntityFinancials/AddFinancialsModal.js
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AVAILABLE_YEARS } from '../../utils/incomeConstants';
import { calculateCorporateTax } from '../../utils/incomeCalculations';
import './AddFinancialsModal.css';

const AddFinancialsModal = ({ year, taxRegime, financials, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    year: year || AVAILABLE_YEARS[0],
    revenue: 0,
    otherIncome: 0,
    purchasesExpenses: 0,
    personnelCosts: 0,
    otherExpenses: 0,
    depreciation: 0,
    financialResult: 0,
    exceptionalResult: 0,
    reserves: 0,
    dividends: 0,
    dividendDecisionDate: '',
    dividendPaymentDate: '',
    isPME: true
  });

  const [calculatedResults, setCalculatedResults] = useState({
    operatingProfit: 0,
    taxableProfit: 0,
    corporateTax: 0,
    netProfit: 0
  });

  useEffect(() => {
    if (financials) {
      // Convert ISO dates to YYYY-MM-DD for date inputs
      const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        return dateStr.split('T')[0];
      };

      setFormData({
        year: financials.year,
        revenue: financials.revenue || 0,
        otherIncome: financials.other_income || 0,
        purchasesExpenses: financials.purchases_expenses || 0,
        personnelCosts: financials.personnel_costs || 0,
        otherExpenses: financials.other_expenses || 0,
        depreciation: financials.depreciation || 0,
        financialResult: financials.financial_result || 0,
        exceptionalResult: financials.exceptional_result || 0,
        reserves: financials.reserves || 0,
        dividends: financials.dividends || 0,
        dividendDecisionDate: formatDateForInput(financials.dividend_decision_date),
        dividendPaymentDate: formatDateForInput(financials.dividend_payment_date),
        isPME: financials.is_pme !== undefined ? financials.is_pme : true
      });
    }
  }, [financials]);

  useEffect(() => {
    const totalRevenue = formData.revenue + formData.otherIncome;
    const totalExpenses = formData.purchasesExpenses + formData.personnelCosts +
      formData.otherExpenses + formData.depreciation;
    const operatingProfit = totalRevenue - totalExpenses;
    const taxableProfit = operatingProfit + formData.financialResult + formData.exceptionalResult;

    let corporateTax = 0;
    let netProfit = taxableProfit;

    if (taxRegime === 'IS' && taxableProfit > 0) {
      const taxResult = calculateCorporateTax(taxableProfit, formData.isPME, formData.year);
      corporateTax = taxResult.totalTax;
      netProfit = taxableProfit - corporateTax;
    }

    setCalculatedResults({
      operatingProfit,
      taxableProfit,
      corporateTax,
      netProfit
    });
  }, [formData, taxRegime]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Handle different input types
    let newValue;
    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'date') {
      newValue = value; // Keep date as string
    } else {
      newValue = parseFloat(value) || 0;
    }
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      ...calculatedResults
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-financials" onClick={e => e.stopPropagation()}>
        <h3>{financials ? 'Modifier les données financières' : 'Ajouter un exercice'}</h3>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h4>Paramètres</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Année</label>
                <select name="year" value={formData.year} onChange={handleChange}>
                  {AVAILABLE_YEARS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              {taxRegime === 'IS' && (
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isPME"
                      checked={formData.isPME}
                      onChange={handleChange}
                    />
                    PME (taux réduit IS 15%)
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h4>Produits</h4>
            <div className="form-group">
              <label>Chiffre d'affaires</label>
              <input
                type="number"
                name="revenue"
                value={formData.revenue}
                onChange={handleChange}
                step="100"
              />
            </div>
            <div className="form-group">
              <label>Autres produits</label>
              <input
                type="number"
                name="otherIncome"
                value={formData.otherIncome}
                onChange={handleChange}
                step="100"
              />
            </div>
          </div>

          <div className="form-section">
            <h4>Charges</h4>
            <div className="form-group">
              <label>Achats et charges externes</label>
              <input
                type="number"
                name="purchasesExpenses"
                value={formData.purchasesExpenses}
                onChange={handleChange}
                step="100"
              />
            </div>
            <div className="form-group">
              <label>Charges de personnel</label>
              <input
                type="number"
                name="personnelCosts"
                value={formData.personnelCosts}
                onChange={handleChange}
                step="100"
              />
            </div>
            <div className="form-group">
              <label>Autres charges</label>
              <input
                type="number"
                name="otherExpenses"
                value={formData.otherExpenses}
                onChange={handleChange}
                step="100"
              />
            </div>
            <div className="form-group">
              <label>Dotations aux amortissements</label>
              <input
                type="number"
                name="depreciation"
                value={formData.depreciation}
                onChange={handleChange}
                step="100"
              />
            </div>
          </div>

          <div className="form-section">
            <h4>Résultats financier et exceptionnel</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Résultat financier</label>
                <input
                  type="number"
                  name="financialResult"
                  value={formData.financialResult}
                  onChange={handleChange}
                  step="100"
                />
              </div>
              <div className="form-group">
                <label>Résultat exceptionnel</label>
                <input
                  type="number"
                  name="exceptionalResult"
                  value={formData.exceptionalResult}
                  onChange={handleChange}
                  step="100"
                />
              </div>
            </div>
          </div>

          <div className="form-section calculation-preview">
            <h4>Calculs automatiques</h4>
            <div className="calculation-row">
              <span>Résultat d'exploitation :</span>
              <span className={`value ${calculatedResults.operatingProfit >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(calculatedResults.operatingProfit)}
              </span>
            </div>
            <div className="calculation-row">
              <span>Résultat avant impôt :</span>
              <span className={`value ${calculatedResults.taxableProfit >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(calculatedResults.taxableProfit)}
              </span>
            </div>
            {taxRegime === 'IS' && (
              <div className="calculation-row">
                <span>Impôt sur les sociétés :</span>
                <span className="value negative">-{formatCurrency(calculatedResults.corporateTax)}</span>
              </div>
            )}
            <div className="calculation-row result">
              <span><strong>Résultat net :</strong></span>
              <span className={`value ${calculatedResults.netProfit >= 0 ? 'positive' : 'negative'}`}>
                <strong>{formatCurrency(calculatedResults.netProfit)}</strong>
              </span>
            </div>
          </div>

          <div className="form-section">
            <h4>Affectation du résultat</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Mise en réserve</label>
                <input
                  type="number"
                  name="reserves"
                  value={formData.reserves}
                  onChange={handleChange}
                  step="100"
                />
              </div>
              <div className="form-group">
                <label>Dividendes</label>
                <input
                  type="number"
                  name="dividends"
                  value={formData.dividends}
                  onChange={handleChange}
                  step="100"
                />
              </div>
            </div>
            {formData.dividends > 0 && (
              <div className="form-row">
                <div className="form-group">
                  <label>Date AG (décision)</label>
                  <input
                    type="date"
                    name="dividendDecisionDate"
                    value={formData.dividendDecisionDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Date versement</label>
                  <input
                    type="date"
                    name="dividendPaymentDate"
                    value={formData.dividendPaymentDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="modal-buttons">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="save-btn save-btn-financials">
              {financials ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default AddFinancialsModal;
